(function () {
  if (customElements.get('lophiel-story-highlights')) return;

  class LophielStoryHighlights extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.dialog = this.querySelector('[data-story-dialog]');
      this.frame = this.querySelector('[data-story-frame]');
      this.stage = this.querySelector('[data-story-stage]');
      this.triggers = [...this.querySelectorAll('[data-story-trigger]')];
      this.progress = this.querySelector('[data-story-progress]');
      this.position = this.querySelector('[data-story-position]');
      this.title = this.querySelector('[data-story-title]');
      this.closeButton = this.querySelector('[data-story-close]');
      this.previousButton = this.querySelector('[data-story-previous]');
      this.nextButton = this.querySelector('[data-story-next]');
      this.muteButton = this.querySelector('[data-story-mute]');
      this.playButton = this.querySelector('[data-story-play]');
      this.pauseIcon = this.querySelector('[data-story-pause-icon]');
      this.playIcon = this.querySelector('[data-story-play-icon]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = this.dataset.storyAutoplay !== 'false';
      this.continueStories = this.dataset.storyContinue !== 'false';
      this.imageDuration = Math.max(3000, Number(this.dataset.storyImageDuration) || 5000);
      this.stories = [...this.querySelectorAll('[data-story-group]')].map((element) => ({
        element,
        title: element.dataset.storyTitle || '',
        slides: [...element.querySelectorAll('[data-story-slide]')],
      })).filter((story) => story.slides.length);

      if (!this.dialog || !this.frame || !this.stage || !this.stories.length) return;

      this.storyIndex = 0;
      this.slideIndex = 0;
      this.muted = true;
      this.userPaused = !this.autoplayEnabled || this.reducedMotion.matches;
      this.paused = this.userPaused;
      this.progressValue = 0;
      this.animationFrame = 0;
      this.pausedForVisibility = false;

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.open(Number(trigger.dataset.storyTrigger), trigger));
      });

      this.closeButton?.addEventListener('click', () => this.dialog.close());
      this.previousButton?.addEventListener('click', () => this.previous());
      this.nextButton?.addEventListener('click', () => this.next(false));
      this.muteButton?.addEventListener('click', () => this.toggleMute());
      this.playButton?.addEventListener('click', () => this.togglePlayback());

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => this.handleClose());
      this.dialog.addEventListener('keydown', (event) => this.handleKeydown(event));

      this.stories.forEach((story) => {
        story.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          if (!video) return;
          video.addEventListener('ended', () => {
            if (this.dialog.open && video === this.activeVideo && !this.paused) this.next(true);
          });
          video.addEventListener('loadedmetadata', () => {
            if (video === this.activeVideo) this.updateVideoProgress(video);
          });
        });
      });

      this.handleReducedMotionChange = (event) => {
        if (!event.matches || !this.dialog.open) return;
        this.userPaused = true;
        this.pause(false);
      };
      this.handleVisibilityChange = () => {
        if (!this.dialog.open) return;
        if (document.hidden && !this.paused) {
          this.pausedForVisibility = true;
          this.pause(false);
        } else if (!document.hidden && this.pausedForVisibility) {
          this.pausedForVisibility = false;
          if (!this.userPaused && !this.reducedMotion.matches) {
            this.paused = false;
            this.startCurrent();
            this.updatePlaybackButton();
          }
        }
      };

      this.reducedMotion.addEventListener?.('change', this.handleReducedMotionChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    disconnectedCallback() {
      this.cancelTick();
      this.reducedMotion?.removeEventListener?.('change', this.handleReducedMotionChange);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    open(index, opener) {
      this.opener = opener;
      this.storyIndex = Math.max(0, Math.min(index, this.stories.length - 1));
      this.slideIndex = 0;
      this.muted = true;
      this.userPaused = !this.autoplayEnabled || this.reducedMotion.matches;
      this.paused = this.userPaused;
      this.progressValue = 0;
      this.pausedForVisibility = false;

      if (!this.dialog.open) this.dialog.showModal();
      this.showCurrent();
      window.requestAnimationFrame(() => this.closeButton?.focus());
    }

    showCurrent() {
      this.stopMedia(true);
      this.progressValue = 0;

      this.stories.forEach((story, storyIndex) => {
        const activeStory = storyIndex === this.storyIndex;
        story.element.hidden = !activeStory;
        story.element.setAttribute('aria-hidden', activeStory ? 'false' : 'true');
        story.slides.forEach((slide, slideIndex) => {
          const activeSlide = activeStory && slideIndex === this.slideIndex;
          slide.hidden = !activeSlide;
          slide.setAttribute('aria-hidden', activeSlide ? 'false' : 'true');
        });
      });

      this.activeStory = this.stories[this.storyIndex];
      this.activeSlide = this.activeStory.slides[this.slideIndex];
      this.activeVideo = this.activeSlide?.querySelector('video') || null;
      this.buildProgress(this.activeStory.slides.length);

      if (this.position) this.position.textContent = `${this.slideIndex + 1} / ${this.activeStory.slides.length}`;
      if (this.title) this.title.textContent = this.activeStory.title;
      if (this.previousButton) this.previousButton.disabled = this.storyIndex === 0 && this.slideIndex === 0;
      if (this.nextButton) {
        const isLast = this.storyIndex === this.stories.length - 1 && this.slideIndex === this.activeStory.slides.length - 1;
        this.nextButton.setAttribute('aria-label', isLast ? 'Close stories' : 'Next story item');
      }

      this.updateMuteButton();
      this.updatePlaybackButton();
      this.startCurrent();
    }

    buildProgress(count) {
      if (!this.progress) return;
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < count; index += 1) {
        const track = document.createElement('span');
        const value = document.createElement('span');
        track.className = 'story-viewer__progress-track';
        value.className = 'story-viewer__progress-value';
        value.style.transform = `scaleX(${index < this.slideIndex ? 1 : 0})`;
        track.appendChild(value);
        fragment.appendChild(track);
      }
      this.progress.replaceChildren(fragment);
      this.progressValues = [...this.progress.querySelectorAll('.story-viewer__progress-value')];
      this.frame.style.setProperty('--story-count', String(count));
    }

    startCurrent() {
      if (!this.activeSlide || this.paused) return;

      if (this.activeVideo) {
        this.activeVideo.muted = this.muted;
        const playPromise = this.activeVideo.play();
        if (playPromise) {
          playPromise.catch(() => {
            this.userPaused = true;
            this.paused = true;
            this.updatePlaybackButton();
          });
        }
        this.tickVideo(this.activeVideo);
        return;
      }

      this.imageStartedAt = performance.now() - this.progressValue * this.imageDuration;
      this.tickImage();
    }

    tickImage() {
      this.cancelTick();
      const step = (time) => {
        if (!this.dialog.open || this.paused || this.activeSlide?.dataset.storyKind === 'video') return;
        this.progressValue = Math.min(1, (time - this.imageStartedAt) / this.imageDuration);
        this.paintProgress(this.progressValue);
        if (this.progressValue >= 1) {
          this.next(true);
          return;
        }
        this.animationFrame = window.requestAnimationFrame(step);
      };
      this.animationFrame = window.requestAnimationFrame(step);
    }

    tickVideo(video) {
      this.cancelTick();
      const step = () => {
        if (!this.dialog.open || this.paused || video !== this.activeVideo) return;
        this.updateVideoProgress(video);
        this.animationFrame = window.requestAnimationFrame(step);
      };
      this.animationFrame = window.requestAnimationFrame(step);
    }

    updateVideoProgress(video) {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      this.progressValue = Math.min(1, video.currentTime / video.duration);
      this.paintProgress(this.progressValue);
    }

    paintProgress(value) {
      const progress = this.progressValues?.[this.slideIndex];
      if (progress) progress.style.transform = `scaleX(${value})`;
    }

    previous() {
      if (this.slideIndex > 0) {
        this.slideIndex -= 1;
      } else if (this.storyIndex > 0) {
        this.storyIndex -= 1;
        this.slideIndex = this.stories[this.storyIndex].slides.length - 1;
      } else {
        return;
      }
      this.paused = this.userPaused || this.reducedMotion.matches;
      this.showCurrent();
    }

    next(fromAutoplay) {
      if (this.slideIndex < this.activeStory.slides.length - 1) {
        this.slideIndex += 1;
      } else {
        this.markViewed(this.storyIndex);
        const canContinue = this.storyIndex < this.stories.length - 1 && (!fromAutoplay || this.continueStories);
        if (!canContinue) {
          this.dialog.close();
          return;
        }
        this.storyIndex += 1;
        this.slideIndex = 0;
      }
      this.paused = this.userPaused || this.reducedMotion.matches;
      this.showCurrent();
    }

    markViewed(index) {
      this.triggers[index]?.classList.add('is-viewed');
    }

    toggleMute() {
      if (!this.activeVideo) return;
      this.muted = !this.muted;
      this.activeVideo.muted = this.muted;
      this.updateMuteButton();
    }

    updateMuteButton() {
      if (!this.muteButton) return;
      const hasVideo = Boolean(this.activeVideo);
      if (!hasVideo && document.activeElement === this.muteButton) this.playButton?.focus();
      this.muteButton.hidden = !hasVideo;
      this.muteButton.setAttribute('aria-pressed', this.muted ? 'true' : 'false');
      this.muteButton.setAttribute('aria-label', this.muted ? 'Unmute story' : 'Mute story');
    }

    togglePlayback() {
      if (this.paused) {
        this.userPaused = false;
        this.paused = false;
        this.startCurrent();
      } else {
        this.userPaused = true;
        this.pause(false);
      }
      this.updatePlaybackButton();
    }

    pause(markAsUser = false) {
      if (markAsUser) this.userPaused = true;
      this.paused = true;
      this.cancelTick();
      this.activeVideo?.pause();
      this.updatePlaybackButton();
    }

    updatePlaybackButton() {
      if (!this.playButton) return;
      this.playButton.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
      this.playButton.setAttribute('aria-label', this.paused ? 'Play story' : 'Pause story');
      if (this.pauseIcon) this.pauseIcon.hidden = this.paused;
      if (this.playIcon) this.playIcon.hidden = !this.paused;
    }

    stopMedia(reset) {
      this.cancelTick();
      this.stories.forEach((story) => {
        story.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          if (!video) return;
          video.pause();
          if (reset) {
            try {
              video.currentTime = 0;
            } catch (_) {}
          }
        });
      });
      this.activeVideo = null;
    }

    cancelTick() {
      if (!this.animationFrame) return;
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
    }

    handleClose() {
      if (this.activeStory && this.progressValue > 0) this.markViewed(this.storyIndex);
      this.stopMedia(true);
      this.paused = true;
      this.userPaused = true;
      this.pausedForVisibility = false;
      this.updatePlaybackButton();
      this.opener?.focus();
      this.opener = null;
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next(false);
      } else if (event.key === ' ' && !event.target.closest('button')) {
        event.preventDefault();
        this.togglePlayback();
      }
    }
  }

  customElements.define('lophiel-story-highlights', LophielStoryHighlights);
})();
